/**
 * Provide methods for scheduling the execution of a callback.
 */
export class Scheduler {

  /**
   * Schedule a callback to be called after some delay.
   *
   * Returns a function that when executed will cancel the scheduled function.
   */
  static schedule(taskFn: () => void, delay: number): () => void {
    const id = setTimeout(taskFn, delay)
    return () => clearTimeout(id)
  }

  /**
   * Schedule a callback to be called before the next render.
   * (If `window.requestAnimationFrame()` is not available, use `scheduler.schedule()` instead.)
   *
   * Returns a function that when executed will cancel the scheduled function.
   */
  static scheduleBeforeRender(taskFn: () => void): () => void {
    if (typeof window === 'undefined') {
      // For SSR just schedule immediately.
      return this.schedule(taskFn, 0)
    }

    if (typeof window.requestAnimationFrame === 'undefined') {
      const frameMs = 16
      return this.schedule(taskFn, frameMs)
    }

    const id = window.requestAnimationFrame(taskFn)
    return () => window.cancelAnimationFrame(id)
  }
}
